import { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { DatePicker, type DateValidationError } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import FieldLabel from './FieldLabel';

interface LabeledDatePickerProps {
  label: string;
  value: string;
  onChange: (iso: string) => void;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  format?: string;
  placeholder?: string;
  /** Optional upper bound. Pass `new Date()` to disallow future dates. */
  maxDate?: Date;
  /** Color for the required asterisk indicator */
  asteriskColor?: string;
  /** Read-only mode — disables typing and the calendar affordance. */
  disabled?: boolean;
}

/** FieldLabel + MUI DatePicker — ISO string in/out */
export default function LabeledDatePicker({
  label,
  value,
  onChange,
  required,
  error,
  helperText,
  format = 'MM/DD/YYYY',
  placeholder,
  maxDate,
  asteriskColor,
  disabled,
}: LabeledDatePickerProps) {
  // The picker owns the in-progress value; only completed dates are published upward.
  //
  // Deriving it from `value` instead cannot work: a part-typed date publishes '', the
  // parent feeds that back, and the derived value becomes null — which resets *every*
  // section. Typing the first digit of a year makes the date year 0001, incomplete by
  // definition, so starting to retype a year wiped the month and day with it.
  const [dayjsValue, setDayjsValue] = useState<Dayjs | null>(() => {
    if (!value) return null;
    const d = dayjs(value);
    return d.isValid() ? d : null;
  });
  // What was last sent up, so the effect below can tell a real external change (form
  // reset, record load) from the echo of our own emit arriving back as a prop.
  const lastPublishedRef = useRef(value);

  useEffect(() => {
    if (value === lastPublishedRef.current) return;
    lastPublishedRef.current = value;
    const d = value ? dayjs(value) : null;
    setDayjsValue(d && d.isValid() ? d : null);
  }, [value]);

  const handleChange = (val: Dayjs | null) => {
    setDayjsValue(val);
    const iso = val && val.isValid() ? val.format('YYYY-MM-DD') : '';
    lastPublishedRef.current = iso;
    onChange(iso);
  };

  // Keyed on the day rather than the Date object: callers pass `new Date()` inline, so
  // a fresh instance arrives on every render and an identity-keyed memo would never hit.
  // Day granularity is all the bound means — MUI compares maxDate with isAfterDay.
  const maxDay = maxDate ? dayjs(maxDate).format('YYYY-MM-DD') : undefined;
  const dayjsMax = useMemo<Dayjs | undefined>(
    () => (maxDay ? dayjs(maxDay) : undefined),
    [maxDay],
  );

  // The calendar already refuses dates past the bound by striking them out, but the field
  // can still be typed into, and a typed date beyond it used to commit in silence.
  //
  // Only the upper bound is reported. MUI's own lower bound is 1900, and every year is
  // below that while it is being typed — 0002, 0020, 0202 on the way to 2025 — so
  // surfacing `minDate` would flash an error through every date entry. An incomplete or
  // impossible date stays silent too, as it was.
  const [beyondMax, setBeyondMax] = useState(false);
  const handleError = (reason: DateValidationError) => {
    setBeyondMax(reason === 'maxDate');
  };

  // Worded for the bound in play: `new Date()` is the only bound either app passes today,
  // and "cannot be in the future" reads better on a date of birth than a printed date.
  const maxMessage =
    maxDay === dayjs().format('YYYY-MM-DD')
      ? 'Date cannot be in the future'
      : `Date cannot be after ${dayjs(maxDay).format(format)}`;

  return (
    <Box>
      <FieldLabel required={required} asteriskColor={asteriskColor}>
        {label}
      </FieldLabel>
      <DatePicker
        value={dayjsValue}
        onChange={handleChange}
        onError={handleError}
        format={format}
        maxDate={dayjsMax}
        disabled={disabled}
        slotProps={{
          textField: {
            fullWidth: true,
            size: 'small',
            placeholder: placeholder ?? format,
            // The bound outranks the caller's message: the caller validates the value it
            // was given, and a date beyond the bound was never handed to it.
            error: beyondMax || error,
            helperText: beyondMax ? maxMessage : helperText,
            inputProps: { 'aria-label': label },
          },
        }}
      />
    </Box>
  );
}
